import React, { useEffect, useState, useCallback } from 'react';
import { supabaseService } from '../../utils/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import { Link } from 'react-router-dom';
import Input from '../../components/ui/Input';

const BookingDashboard = () => {
  const { user, role } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ eventDate: '', baseFee: '', counterpartId: '' });
  const [offerForm, setOfferForm] = useState({ bookingId: '', proposedFee: '', commissionRate: '', message: '' });
  const [commissions, setCommissions] = useState([]);
  const [processingOffer, setProcessingOffer] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await supabaseService.listBookingsForCurrentUser(user.id);
      setBookings(data);
      // Charger commissions si user agent ou artiste
      if (['agent','artist'].includes(role)) {
        if (!window || !window.supabase) {
          // supabase global non défini: on ignore silencieusement
        } else {
        const { data: commData, error } = await window.supabase
          .from('commissions')
          .select('*')
          .or(`agent_id.eq.${user.id},artist_id.eq.${user.id}`)
          .order('created_at', { ascending: false })
          .limit(50);
        if (!error) setCommissions(commData || []);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user, role]);

  useEffect(() => { load(); }, [load]);

  const createDraft = async () => {
    setCreating(true);
    setError(null);
    try {
      const booking = await supabaseService.createDraftBooking({
        currentUserId: user.id,
        role,
        counterpartId: form.counterpartId || null,
        eventDate: form.eventDate || null,
        baseFee: form.baseFee ? parseInt(form.baseFee, 10) : null,
        notes: 'Draft created via dashboard'
      });
      setBookings([booking, ...bookings]);
      setForm({ eventDate: '', baseFee: '', counterpartId: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const createOffer = async () => {
    setProcessingOffer(true);
    setError(null);
    try {
      await supabaseService.createBookingOffer({
        bookingId: offerForm.bookingId,
        proposerId: user.id,
        proposedFee: offerForm.proposedFee,
        commissionRate: offerForm.commissionRate,
        message: offerForm.message
      });
      setOfferForm({ bookingId: '', proposedFee: '', commissionRate: '', message: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessingOffer(false);
    }
  };

  const confirm = async (id) => {
    setError(null);
    try {
      const updated = await supabaseService.confirmBooking({ bookingId: id });
      setBookings(bs => bs.map(b => b.id === id ? updated : b));
    } catch (e) {
      setError(e.message);
    }
  }

  const markPerformed = async (id) => {
    setError(null);
    try {
      const updated = await supabaseService.markBookingPerformed({ bookingId: id });
      setBookings(bs => bs.map(b => b.id === id ? updated : b));
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Bookings</h1>
      <p className="text-sm text-muted-foreground mb-6">Vue minimaliste pour tester le socle booking (RLS et insertion). Rôle actuel: <span className="font-medium">{role}</span></p>
      {error && <div className="mb-4 text-sm text-red-500">{error}</div>}
  <div className="mb-8 border border-border rounded-lg p-4 space-y-4">
        <h2 className="font-semibold">Créer un booking draft</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <Input type="date" value={form.eventDate} onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))} placeholder="Date" />
          <Input type="number" value={form.baseFee} onChange={e => setForm(f => ({ ...f, baseFee: e.target.value }))} placeholder="Cachet (EUR)" />
          <Input value={form.counterpartId} onChange={e => setForm(f => ({ ...f, counterpartId: e.target.value }))} placeholder="Counterpart user id (optionnel)" />
          <Button onClick={createDraft} disabled={creating || !user}>{creating ? 'Création...' : 'Créer'}</Button>
        </div>
        <p className="text-xs text-muted-foreground">Selon votre rôle, les colonnes artist_id / venue_id / agent_id sont automatiquement renseignées. Ajoutez un user_id counterpart si connu pour lier immédiatement.</p>
      </div>
      <div className="mb-8 border border-border rounded-lg p-4 space-y-4">
        <h2 className="font-semibold">Créer une offre (booking_offers)</h2>
        <div className="grid md:grid-cols-5 gap-4">
          <Input value={offerForm.bookingId} onChange={e=>setOfferForm(f=>({...f,bookingId:e.target.value}))} placeholder="Booking ID" />
          <Input type="number" value={offerForm.proposedFee} onChange={e=>setOfferForm(f=>({...f,proposedFee:e.target.value}))} placeholder="Proposed Fee" />
          <Input type="number" value={offerForm.commissionRate} onChange={e=>setOfferForm(f=>({...f,commissionRate:e.target.value}))} placeholder="Commission %" />
          <Input value={offerForm.message} onChange={e=>setOfferForm(f=>({...f,message:e.target.value}))} placeholder="Message" />
          <Button onClick={createOffer} disabled={processingOffer || !offerForm.bookingId}>{processingOffer ? 'Envoi...' : 'Proposer'}</Button>
        </div>
        <p className="text-xs text-muted-foreground">Utilise un ID de booking existant (statut draft/proposed) pour tester la négociation. La commission (%) est facultative.</p>
      </div>

      <div>
        <h2 className="font-semibold mb-3">Vos 50 derniers bookings</h2>
        {loading ? <div>Chargement...</div> : (
          <div className="space-y-2">
            {bookings.map(b => (
              <div key={b.id} className="border border-border rounded-md p-3 text-sm flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex-1">
                  <div className="font-medium"><Link className="underline" to={`/bookings/${b.id}`}>{b.event_date || 'Date à définir'} — {b.status}</Link></div>
                  <div className="text-muted-foreground text-xs">ID: {b.id}</div>
                </div>
                <div className="mt-2 md:mt-0 text-xs text-muted-foreground space-y-1 md:text-right">
                  {b.artist_id && <div>Artist: {b.artist_id}</div>}
                  {b.venue_id && <div>Venue: {b.venue_id}</div>}
                  {b.agent_id && <div>Agent: {b.agent_id}</div>}
                  {b.base_fee && <div>Fee: {b.base_fee} €</div>}
                  {b.status !== 'confirmed' && b.status !== 'performed' && b.base_fee && <Button size="sm" variant="accent" onClick={() => confirm(b.id)}>Confirmer</Button>}
                  {b.status === 'confirmed' && <Button size="sm" variant="outline" onClick={() => markPerformed(b.id)}>Marquer performed</Button>}
                </div>
              </div>
            ))}
            {!bookings.length && <div className="text-xs text-muted-foreground">Aucun booking.</div>}
          </div>
        )}
      </div>

      {commissions.length > 0 && (
        <div className="mt-10">
          <h2 className="font-semibold mb-3">Commissions</h2>
          <div className="space-y-2">
            {commissions.map(c => (
              <div key={c.id} className="border border-border rounded-md p-3 text-xs flex justify-between items-center gap-4">
                <div>
                  <div>ID: {c.id}</div>
                  <div className="text-muted-foreground">Booking: {c.booking_id}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground">{c.status} {c.amount_calculated && `| ${c.amount_calculated}€`}</span>
                  {c.status !== 'paid' && (
                    <Button size="sm" variant="outline" onClick={async ()=>{
                      try {
                        await supabaseService.updateCommissionStatus({ commissionId: c.id, status: 'paid' });
                        setCommissions(cs => cs.map(x => x.id === c.id ? { ...x, status: 'paid', paid_at: new Date().toISOString() } : x));
                      } catch(e) { setError(e.message); }
                    }}>Payer</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDashboard;